import lodash from 'lodash';
import { binderPermissionsSelector } from 'common/permissions/selectors';
import { doaFeatureAccess } from '../features/predicates';

export const actions = {
    VIEW: 'view',
    UPLOAD: 'upload',
    MANAGE: 'manage',
};

export const features = {
    METADATA: 'metadata',
    DOCUMENTS: 'documents',
    USERS: 'users',
    ADMINS: 'admins',
    TASKS: 'tasks',
    EXPECTATIONS: 'expectations',
    EVENTS: 'events',
    DELEGATION: 'delegation',
    DASHBOARD: 'dashboard',
    TEAM: 'team',
    MONITORS: 'monitors'
};

export function canAccessStudyPermissions(studyPermissions, action, feature) {
    return lodash.some(
        studyPermissions,
        perm => perm.action === action && perm.feature === feature
    );
}

export const canAccessStudyPermissionPredicate = (state, action, feature) => {
    const studyPermissions = lodash.get(state, 'me.studyPermissions', {});
    return canAccessStudyPermissions(studyPermissions, action, feature) || overrideStudyPredicate(state);
};

export const viewStudyExpectationsPredicate = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.VIEW, features.EXPECTATIONS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.EXPECTATIONS);
};

export const viewStudyMonitorsPredicate = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.VIEW, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.VIEW, features.MONITORS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.MONITORS);
};

export const viewStudyTeamPredicate = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.VIEW, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.VIEW, features.TEAM) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.TEAM);
};
export const viewStudyTeamMemberPredicate = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.VIEW, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.VIEW, features.TEAM) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.TEAM) ||
        canAccessStudyPermissionPredicate(state, actions.VIEW, features.MONITORS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.MONITORS);
};

export const manageStudyExpectationsPredicate = (state) => canAccessStudyPermissionPredicate(state, actions.MANAGE, features.EXPECTATIONS);

export const manageStudyTeamMemberPredicate = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.MANAGE, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.TEAM);
};

export const manageStudyTeamMemberPredicateOld = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.MANAGE, features.USERS);
};

export const manageStudyTeamMemberPredicateNew = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.MANAGE, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.TEAM);
};

export const manageStudyMonitorsPredicate = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.MANAGE, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.MONITORS);
};

export const manageStudyMonitorsPredicateOld = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.MANAGE, features.USERS);
};

export const manageStudyMonitorsPredicateNew = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.MANAGE, features.USERS) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.MONITORS);
};

export const overrideStudyPredicate = (state) => {
    const currentUserId = lodash.get(state, 'me.id', []);
    const createdBy = lodash.get(state, 'study.createdBy', []);

    return currentUserId === createdBy;
};

export const manageStudyMetadataPredicate = (state) => canAccessStudyPermissionPredicate(state, actions.MANAGE, features.METADATA);
export const manageStudyAdminsPredicate = (state) => canAccessStudyPermissionPredicate(state, actions.MANAGE, features.ADMINS);
export const viewStudyMetadataPredicate = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.VIEW, features.METADATA) ||
        canAccessStudyPermissionPredicate(state, actions.MANAGE, features.METADATA);
};

export const viewStudySettingsPredicate = (state) => {
    return viewStudyMetadataPredicate(state) ||
        viewStudyTeamMemberPredicate(state) ||
        manageStudyExpectationsPredicate(state);
};

export const viewStudyDashboardPredicate = (state) => canAccessStudyPermissionPredicate(state, actions.VIEW, features.DASHBOARD);

export const getDefaultSettingsRoute = (state, studyId) => {
    if (viewStudyMetadataPredicate(state)) {
        return `/study/${studyId}/settings/study`;
    }
    if (viewStudyTeamMemberPredicate(state)) {
        return `/study/${studyId}/settings/team`;
    }
    if (manageStudyExpectationsPredicate(state)) {
        return `/study/${studyId}/settings/expectations`;
    }
};

export const getDefaultStudyRoute = (state, studyId) => {
    if (viewStudyDashboardPredicate(state)) {
        return `/study/${studyId}/dashboard`;
    }
    return `/study/${studyId}/documents`;
};

export const uploadDocumentPredicate = (state) => {
    if (lodash.get(state, 'study.id'))
        return canAccessStudyPermissionPredicate(state, actions.UPLOAD, features.DOCUMENTS);
    const binderPermissions = binderPermissionsSelector(state);
    return binderPermissions.upload;
};

export const viewStudyEventsPredicate = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.VIEW, features.EVENTS) ||
        manageStudyEventsPredicate(state);
};

export const manageStudyEventsPredicate = (state) => canAccessStudyPermissionPredicate(state, actions.MANAGE, features.EVENTS);
export const manageStudyTasksPredicate = (state) => canAccessStudyPermissionPredicate(state, actions.MANAGE, features.TASKS);

export const manageStudyDelegationPredicate = (state) => canAccessStudyPermissionPredicate(state, actions.MANAGE, features.DELEGATION);

export const viewStudyDelegationPredicate = (state) => {
    return canAccessStudyPermissionPredicate(state, actions.VIEW, features.DELEGATION) ||
        manageStudyDelegationPredicate(state);
};

export const userCanAccessStudyDelegation = (state) => {
    return viewStudyDelegationPredicate(state) && doaFeatureAccess(state);
};

export const manageAdminsPredicate = (state) => canAccessStudyPermissionPredicate(state, actions.MANAGE, features.ADMINS);